import { useState, useCallback } from 'react'

/**
 * Return type for useBookSelection hook
 */
interface UseBookSelectionReturn {
    // State
    selectedIds: Set<string>
    showBulkEditModal: boolean
    setShowBulkEditModal: (show: boolean) => void
    showBatchAddListModal: boolean
    setShowBatchAddListModal: (show: boolean) => void
    showBulkDeleteConfirm: boolean
    setShowBulkDeleteConfirm: (show: boolean) => void

    // Functions
    toggleSelection: (bookId: string) => void
    selectAll: (bookIds: string[]) => void
    clearSelection: () => void
    removeFromSelection: (bookId: string) => void
}

/**
 * Custom hook for managing book selection and bulk operations
 */
export function useBookSelection(): UseBookSelectionReturn {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [showBulkEditModal, setShowBulkEditModal] = useState(false)
    const [showBatchAddListModal, setShowBatchAddListModal] = useState(false)
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

    // Toggle selection for a single book
    const toggleSelection = useCallback((bookId: string) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(bookId)) {
                newSet.delete(bookId)
            } else {
                newSet.add(bookId)
            }
            return newSet
        })
    }, [])

    // Select all books
    const selectAll = useCallback((bookIds: string[]) => {
        setSelectedIds(new Set(bookIds))
    }, [])

    // Clear all selections
    const clearSelection = useCallback(() => {
        setSelectedIds(new Set())
    }, [])

    // Remove a book from selection (used after deletion)
    const removeFromSelection = useCallback((bookId: string) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev)
            newSet.delete(bookId)
            return newSet
        })
    }, [])

    return {
        // State
        selectedIds,
        showBulkEditModal,
        setShowBulkEditModal,
        showBatchAddListModal,
        setShowBatchAddListModal,
        showBulkDeleteConfirm,
        setShowBulkDeleteConfirm,

        // Functions
        toggleSelection,
        selectAll,
        clearSelection,
        removeFromSelection,
    }
}
